import { useState, useRef, useEffect } from 'react';
import { isEqual } from 'radash';
import { ElementsTrack } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/ElementsTrack';
import { Track } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Track/Track';
import { ElementLanes } from '../types';
import { VerticalDragState } from '../../hooks/useVerticalDrag';
import { generateElementLanes } from '@/Pages/Clip/ClipPlayer/ClipAssets/utils';

export const useElementLanesState = (playerConfigElementTracks: ElementsTrack[]) => {
  const [elementLanes, setElementLanes] = useState<ElementLanes>([]);
  const [draggingElementId, setDraggingElementId] = useState<string | null>(null);
  const [verticalDragState, setVerticalDragState] = useState<VerticalDragState | null>(null);
  const [isVerticalDragging, setIsVerticalDragging] = useState<boolean>(false);

  const elementLanesRef = useRef<ElementLanes | null>(null);
  const initialFramesRef = useRef<{ fromFrame: number; toFrame: number } | null>(null);
  const prevPlayerConfigElementTracksRef = useRef<ElementsTrack[] | null>(null);
  const isUpdatingFromVerticalDragRef = useRef<boolean>(false);

  useEffect(() => {
    if (isUpdatingFromVerticalDragRef.current) {
      isUpdatingFromVerticalDragRef.current = false;
      return;
    }

    if (!playerConfigElementTracks || playerConfigElementTracks.length === 0) {
      return;
    }

    if (isEqual(prevPlayerConfigElementTracksRef.current, playerConfigElementTracks)) {
      return;
    }

    prevPlayerConfigElementTracksRef.current = playerConfigElementTracks;
    setElementLanes(generateElementLanes(playerConfigElementTracks as Track[]));
  }, [playerConfigElementTracks]);

  const resetDragState = () => {
    setDraggingElementId(null);
    setIsVerticalDragging(false);
    setVerticalDragState(null);
    initialFramesRef.current = null;
    elementLanesRef.current = null;
  };

  return {
    elementLanes,
    setElementLanes,
    draggingElementId,
    setDraggingElementId,
    verticalDragState,
    setVerticalDragState,
    isVerticalDragging,
    setIsVerticalDragging,
    elementLanesRef,
    initialFramesRef,
    isUpdatingFromVerticalDragRef,
    resetDragState
  };
};
